import { I18n } from '@affine/i18n';

/** The statuses of a goal run, as the server reports them. */
const GOAL_RUN_STATUSES = [
  'queued',
  'running',
  'working',
  'paused',
  'waiting_for_confirmation',
  'waiting_for_user',
  'completed',
  'failed',
  'cancelled',
] as const;

type GoalRunStatus = (typeof GOAL_RUN_STATUSES)[number];

function isGoalRunStatus(status?: string | null): status is GoalRunStatus {
  return (GOAL_RUN_STATUSES as readonly string[]).includes(status ?? '');
}

/** The goal takes its next step or is about to; a chat goal "works" while its turn runs in the chat. */
export function isGoalBusy(status?: string | null): boolean {
  return status === 'queued' || status === 'running' || status === 'working';
}

/** The goal waits for the user to confirm or answer. */
export function isGoalWaitingForUser(status?: string | null): boolean {
  return status === 'waiting_for_confirmation' || status === 'waiting_for_user';
}

export function isGoalEnded(status?: string | null): boolean {
  return (
    status === 'completed' || status === 'failed' || status === 'cancelled'
  );
}

/** The goal has not ended: it works, waits or is paused. */
export function isGoalOpen(status?: string | null): boolean {
  return (
    isGoalBusy(status) || isGoalWaitingForUser(status) || status === 'paused'
  );
}

export function goalRunStatusTone(status?: string | null): string {
  if (status === 'completed') return 'positive';
  if (status === 'failed' || status === 'cancelled') return 'critical';
  if (isGoalWaitingForUser(status)) return 'warning';
  if (isGoalBusy(status)) return 'accent';
  return 'muted';
}

export function goalRunStatusLabel(status?: string | null): string {
  const key = isGoalRunStatus(status) ? status.replaceAll('_', '-') : 'unknown';
  return I18n.t(`com.streamlain.ai.target.run-status.${key}`);
}
